import { api } from './api.ts';
import { importPublicKey, unwrapDek, wrapDekFor, type KeyAlgorithm } from './crypto/keypair.ts';
import { requireVault } from './crypto/vault.ts';
import { fromBase64, toBase64 } from './crypto/primitives.ts';
import { listDocuments, type DocumentMeta } from './documents.ts';

/**
 * Vertrauensperson / Notfallzugang (Entscheidung Karsten, 10.08.2026).
 *
 * Keine neue Kryptographie: `wrapDekFor`/`unwrapDek` sind bereits generisch
 * für jeden Empfänger nutzbar (siehe `documents.ts`, `grantAccess()`), hier
 * kommt nur ein dritter Aufrufort dazu.
 */

export interface TrusteeRow {
  id: string;
  status: 'eingeladen' | 'bestaetigt' | 'notfall_angefordert' | 'freigegeben' | 'widerrufen';
  wartezeit_tage: number;
  eingeladen_am: string;
  bestaetigt_am: string | null;
  notfall_angefordert_am: string | null;
  notfall_freizuschalten_ab: string | null;
  freigegeben_am: string | null;
  display_name: string;
  email: string;
  schluessel_bereit: boolean;
}

export async function ladeEigeneVertrauenspersonen(): Promise<TrusteeRow[]> {
  const { trustees } = await api.get<{ trustees: TrusteeRow[] }>('/api/trustees');
  return trustees;
}

export interface TrusteeBeziehung {
  id: string;
  status: TrusteeRow['status'];
  wartezeit_tage: number;
  notfall_angefordert_am: string | null;
  notfall_freizuschalten_ab: string | null;
  freigegeben_am: string | null;
  customer_id: string;
  customer_name: string;
}

export async function ladeVertrauensBeziehungenAlsPerson(): Promise<TrusteeBeziehung[]> {
  const { relationships } = await api.get<{ relationships: TrusteeBeziehung[] }>(
    '/api/trustees/for-trustee',
  );
  return relationships;
}

export async function ladeVertrauensperson(email: string, displayName: string, wartezeitTage: number) {
  return api.post<{
    customerTrusteeId: string;
    status: string;
    mailVersendet: boolean;
    einrichtungscode?: string;
    link?: string;
  }>('/api/trustees', { email, displayName, wartezeitTage });
}

export async function widerrufeVertrauensperson(customerTrusteeId: string): Promise<void> {
  await api.post(`/api/trustees/${customerTrusteeId}/revoke`);
}

/**
 * Bestätigung einer Einladung: entsperrt jeden eigenen Dokument-DEK und
 * wrappt ihn neu für die Vertrauensperson — exaktes Muster aus
 * `grantAccess()` in `documents.ts`, nur mit einem dauerhaften statt einem
 * befristeten Empfänger.
 */
export async function bestaetigeVertrauensperson(customerTrusteeId: string): Promise<number> {
  const vault = requireVault();

  const { trustees } = await api.get<{
    trustees: Array<{
      customerTrusteeId: string;
      algorithm: KeyAlgorithm;
      publicKey: string;
    }>;
  }>('/api/keys/my-trustees/public');
  const trustee = trustees.find((t) => t.customerTrusteeId === customerTrusteeId);
  if (!trustee) {
    throw new Error(
      'Die Vertrauensperson hat ihr eigenes Passwort noch nicht eingerichtet — bitte später erneut versuchen.',
    );
  }

  const trusteePublicRaw = fromBase64(trustee.publicKey);
  await importPublicKey(trusteePublicRaw, trustee.algorithm);   // frühes Fehlschlagen bei defektem Schlüssel

  const documents = await listDocuments();
  const wraps = [];
  for (const meta of documents) {
    const dek = await unwrapDek(fromBase64(meta.wrapped_dek), vault.privateKey, vault.algorithm);
    wraps.push({
      documentId: meta.id,
      wrappedDekTrustee: toBase64(await wrapDekFor(dek, trusteePublicRaw, trustee.algorithm)),
    });
  }

  await api.post(`/api/trustees/${customerTrusteeId}/wraps`, { wraps });
  return wraps.length;
}

/**
 * Dokumente einer freigegebenen Vertrauensperson-Beziehung — eigener
 * Endpunkt, getrennt von den eigenen Dokumenten (`listDocuments()`), damit
 * sich beide Listen nicht vermischen, wenn dasselbe Konto zugleich Kunde
 * und Vertrauensperson ist.
 */
export async function ladeDokumenteAlsVertrauensperson(
  customerTrusteeId: string,
): Promise<DocumentMeta[]> {
  const { documents } = await api.get<{ documents: DocumentMeta[] }>(
    `/api/trustees/${customerTrusteeId}/documents`,
  );
  return documents;
}

/** Notfallzugriff anfordern — startet die vom Kunden festgelegte Wartezeit. */
export async function notfallzugriffAnfordern(
  customerTrusteeId: string,
): Promise<{ ok: boolean; widerspruch_link?: string }> {
  return api.post<{ ok: boolean; widerspruch_link?: string }>(
    `/api/trustees/${customerTrusteeId}/notfall-anfordern`,
  );
}

/**
 * Widerspruch durch den Kunden — öffentlich, ohne Anmeldung, nur mit dem
 * Token aus der Mail (Gegenstück zu `notfallzugriffAnfordern`).
 */
export async function notfallzugriffWidersprechen(token: string): Promise<void> {
  await api.post('/api/trustees/notfall-widersprechen', { token });
}
